import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Link from "@mui/material/Link";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageTopBar from "../../../components/common/PageTopBar";
import { toolbarButtonSx, toolbarFieldsSx } from "../../../components/common/toolbarStyles";
import { FacultyNoGradesMessage, useFacultyHasNoGrades } from "../../../modules/common/shared/FacultyNoGrades";
import { GradeSectionComponent } from "../../../modules/common/shared/GradeSectionComponent";
import { UserRole, STUDENT_STATUS } from "../../../modules/common/shared/types";
import { getLoggedInUserRole } from "../../../modules/common/user/UserService";
import { useFeeDues } from "../hooks/useFeeDues";
import { FeeDue } from "../types";

/** Page message shown in place of the table (before the first Get, or when nothing was found). */
const PageMessage = ({ text }: { text: string }) => (
  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
    {text}
  </Typography>
);

/**
 * Finance → Outstanding for admin and faculty: Grade / Section (/ Status for admin) + Get in the top
 * bar, results below. `canPay` = false makes it view-only (faculty).
 */
export const AdminFeeDuesView = ({ canPay = true }: { canPay?: boolean }) => {
  const [grade, setGrade] = useState("");
  const [section, setSection] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  // what the shown result was fetched for (the filters may have changed since)
  const [searched, setSearched] = useState<{ grade: string; section: string; status: string } | null>(null);

  const navigate = useNavigate();
  const noGrades = useFacultyHasNoGrades();
  const isFaculty = getLoggedInUserRole() === UserRole.FACULTY;

  const { fetched, feeDues, loading, error, fetchFeeDues } = useFeeDues(grade, section, status);

  const handleGet = (event: React.FormEvent) => {
    event.preventDefault();
    setSearched({ grade, section, status });
    fetchFeeDues(grade, section, status);
  };

  const describe = (query: { grade: string; section: string; status: string }) =>
    `${query.grade} – ${query.section}${isFaculty ? "" : ` (${query.status})`}`;

  return (
    <Box sx={{ mt: 2, mr: 2 }}>
      <PageTopBar
        left="Outstanding"
        right={
          !noGrades && (
            <Box component="form" display="flex" alignItems="center" gap={1} flexWrap="wrap" onSubmit={handleGet} sx={toolbarFieldsSx}>
              <GradeSectionComponent
                grade={grade}
                setGrade={setGrade}
                section={section}
                setSection={setSection}
                status={status}
                setStatus={setStatus}
                addGraduate={true}
                statusListRequired={true}
                statusList={[STUDENT_STATUS.Active, STUDENT_STATUS.Dropped]}
                inline
              />
              <Button type="submit" variant="outlined" size="small" sx={toolbarButtonSx}>
                Get
              </Button>
            </Box>
          )
        }
      />

      {noGrades ? (
        <FacultyNoGradesMessage />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      ) : !fetched ? (
        <PageMessage text="Select grade and section, then click Get." />
      ) : feeDues.length === 0 ? (
        <PageMessage text={`No outstanding dues for ${searched ? describe(searched) : "the selected class"}.`} />
      ) : isFaculty ? (
        <TableContainer className="mui-table-container-style">
          <Table size="small" className="mui-table-bordered">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Terms Due</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feeDues.map((feeDue: FeeDue) => (
                <TableRow key={feeDue.studentName + feeDue.gradeName + feeDue.terms + feeDue.guardianPhoneNo}>
                  <TableCell>{feeDue.studentName}</TableCell>
                  <TableCell>{feeDue.terms}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer className="mui-table-container-style">
          <Table size="small" className="mui-table-bordered">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Admission No</TableCell>
                <TableCell>Guardian Phone No</TableCell>
                <TableCell>Previous Dues</TableCell>
                <TableCell>Terms</TableCell>
                <TableCell>Payable</TableCell>
                {canPay && <TableCell>Pay</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {feeDues.map((feeDue: FeeDue) => (
                <TableRow key={feeDue.studentName + feeDue.gradeName + feeDue.terms + feeDue.guardianPhoneNo}>
                  <TableCell>{feeDue.studentName}</TableCell>
                  <TableCell>{feeDue.admissionNo}</TableCell>
                  <TableCell>{feeDue.guardianPhoneNo}</TableCell>
                  <TableCell>{feeDue.previousTotalDue > 0 ? feeDue.previousTotalDue : undefined}</TableCell>
                  <TableCell>{feeDue.terms}</TableCell>
                  <TableCell>{feeDue.payable}</TableCell>
                  {canPay && (
                    <TableCell>
                      <Link component="button" variant="body2" onClick={() => navigate("/app/fees/pay", { state: { feeDue } })}>
                        Pay
                      </Link>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
